import { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm, type FieldErrors } from 'react-hook-form'
import { NotePencilIcon } from '@phosphor-icons/react'
import { useMediaQuery } from '@mui/material'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { emotionTheme } from '@styles/theme'
import { getDeviationPolarity, isDeviationMandatory } from './helpers/validateDeviationRules'
import { initialFormValues } from './form/initialFormValues'
import { Input } from '@components/shared/Input'
import { ReturnModal } from '@components/modals/ReturnModal'
import { RowBox } from '@components/shared/RowBox'
import { Select } from '@components/shared/Select'
import { SeparatorWithLine } from '@components/shared/SeparatorWithLine'
import { setInitialValues } from './form/setInitialValues'
import { Textarea } from '@components/shared/Textarea'
import { Tooltip } from '@components/shared/Tooltip'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { usePreview } from '@pages/Preview/context'
import { getDeviationJustification, saveDeviationJustification, updateDeviationJustification } from '@services/api/preview'
import { toast } from '@utils/functions/notificationToast'

import type { JustifyingDeviationsModalProps } from './types'
import type { DeviationJustificationFormProps } from './form/types'
import type { GetDeviationJustificationResponseProps } from '@services/api/preview/types'

import * as S from './styles'

export const JustifyingDeviationsModal = ({
  data,
  open,
  onClose,
  onSave,
  isReadOnly: isReadOnlyProp = false,
  userCanEdit = false,
  currentUserId = '',
  currentWeek,
  selectedWeek,
  branchCode,
  economicGroupCode,
  accountId,
  referenceDate,
  ...rest
}: JustifyingDeviationsModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)
  const { deviationTypesBudget, deviationTypesS1, setDeviationTypesBudget, setDeviationTypesS1, getDeviationTypes } =
    usePreview()

  const methods = useForm<DeviationJustificationFormProps>({
    defaultValues: initialFormValues
  })

  const [openCancelModal, setOpenCancelModal] = useState(false)
  const [justification, setJustification] = useState<GetDeviationJustificationResponseProps | null>(null)
  const [isJustificationResolved, setIsJustificationResolved] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [justificationId, setJustificationId] = useState<number | null>(null)

  // RF06: Determina se é read-only baseado em permissões
  // CA01: Apenas o criador da justificativa pode editar
  const isReadOnly = useMemo(() => {
    if (isReadOnlyProp) return true

    // Se há dados criados anteriormente
    if (data?.createdBy) {
      // Apenas o próprio usuário que criou pode editar
      return data.createdBy !== currentUserId
    }

    // Se é uma semana passada, é read-only
    if (selectedWeek && currentWeek) {
      return Number(selectedWeek) < Number(currentWeek)
    }

    // Se não tem permissão de editar
    return !userCanEdit
  }, [isReadOnlyProp, data?.createdBy, currentUserId, selectedWeek, currentWeek, userCanEdit])

  // Verifica obrigatoriedade baseada nas regras de negócio
  const isBudgetDeviationMandatory = useMemo(() => {
    if (!data) return false
    return isDeviationMandatory(data.deviationVsBudget || 0, data.deviationVsBudgetPercentage || 0)
  }, [data])

  const isWeekDeviationMandatory = useMemo(() => {
    if (!data) return false
    return isDeviationMandatory(data.deviationVsPreviewWeek || 0, data.deviationVsPreviewWeekPercentage || 0)
  }, [data])

  // Determina polaridade de cada desvio (para filtrar tipos de desvio na API).
  // A referência é a resposta do GET /api/deviationjustification (`deviationValueBudget` para o
  // bloco vs Budget e `deviationPreviewAmountS1` para o bloco vs Prévia S-1); se a justificativa
  // ainda não existe, cai para os valores da linha do grid.
  const budgetIsPositive = useMemo(() => {
    const value = justification?.deviationValueBudget ?? data?.deviationVsBudget ?? 0
    return getDeviationPolarity(value) === 'positive'
  }, [justification?.deviationValueBudget, data?.deviationVsBudget])

  const weekIsPositive = useMemo(() => {
    const value = justification?.deviationPreviewAmountS1 ?? data?.deviationVsPreviewWeek ?? 0
    return getDeviationPolarity(value) === 'positive'
  }, [justification?.deviationPreviewAmountS1, data?.deviationVsPreviewWeek])

  // Carrega os tipos de desvio dos dois campos (uma chamada por polaridade), somente após
  // resolver a justificativa — é ela que define o `isPositive` de cada chamada.
  useEffect(() => {
    if (!open || !accountId || !isJustificationResolved) return

    getDeviationTypes(accountId, budgetIsPositive, weekIsPositive)
  }, [open, accountId, isJustificationResolved, budgetIsPositive, weekIsPositive, getDeviationTypes])

  // Mapeia DeviationTypeOption para SelectOption
  const budgetSelectOptions = deviationTypesBudget.map((opt) => ({
    label: opt.description,
    value: opt.code
  }))
  const weekSelectOptions = deviationTypesS1.map((opt) => ({
    label: opt.description,
    value: opt.code
  }))

  const onError = (errors: FieldErrors<DeviationJustificationFormProps>) => {
    console.log({ errors })
  }

  const onSubmit = async (formData: DeviationJustificationFormProps) => {
    if (!data || !branchCode || !referenceDate || economicGroupCode === undefined || accountId === undefined) return

    // Validar obrigatoriedade antes de salvar
    if (isBudgetDeviationMandatory && !formData.deviationTypeBudget) {
      methods.setError('deviationTypeBudget', {
        type: 'required',
        message: 'Campo obrigatório'
      })
      return
    }

    if (isWeekDeviationMandatory && !formData.deviationTypeWeek) {
      methods.setError('deviationTypeWeek', {
        type: 'required',
        message: 'Campo obrigatório'
      })
      return
    }

    const budgetTypeId = deviationTypesBudget.find((t) => t.code === formData.deviationTypeBudget)?.id ?? null
    const weekTypeId = deviationTypesS1.find((t) => t.code === formData.deviationTypeWeek)?.id ?? null
    const weekNumber = Number((data as any).weekNumber ?? data.week)

    const deviationValues = {
      deviationValueBudget: data.deviationVsBudget,
      deviationPercentBudget: data.deviationVsBudgetPercentage,
      deviationTypeBudgetId: budgetTypeId,
      observationsBudget: formData.observationsBudget || null,
      deviationValueS1: data.deviationVsPreviewWeek,
      deviationPercentS1: data.deviationVsPreviewWeekPercentage,
      deviationTypeS1Id: weekTypeId,
      observationsS1: formData.observationsWeek || null
    }

    setIsSaving(true)
    try {
      const response = justificationId
        ? await updateDeviationJustification(justificationId, deviationValues)
        : await saveDeviationJustification({ branchCode, economicGroupCode, accountId, weekNumber, referenceDate, ...deviationValues })

      if (!response.success) {
        toast.error(response.errorMessage || 'Erro ao salvar justificativa de desvio.')
        return
      }

      toast.success('Justificativa de desvio salva com sucesso!')
      await onSave?.({
        ...data,
        deviationTypeBudget: formData.deviationTypeBudget,
        observationsBudget: formData.observationsBudget,
        deviationTypeWeek: formData.deviationTypeWeek,
        observationsWeek: formData.observationsWeek
      })
      onClose(false)
    } catch (error: any) {
      toast.error(error?.response?.data?.errorMessage || 'Erro ao salvar justificativa de desvio.')
    } finally {
      setIsSaving(false)
    }
  }

  // Pré-popula formulário com dados estáticos da linha do grid
  useEffect(() => {
    const initializeForm = async () => {
      if (!data || !open) return

      const formValues = setInitialValues(data)
      methods.reset(formValues)

      const weekNumber = Number((data as any).weekNumber ?? data.week)
      if (branchCode && referenceDate && economicGroupCode !== undefined && accountId !== undefined && weekNumber) {
        try {
          const savedJustification = await getDeviationJustification({
            branchCode,
            economicGroupCode,
            accountId,
            weekNumber,
            referenceDate
          })

          if (savedJustification) {
            if (savedJustification.id > 0) setJustificationId(savedJustification.id)
            setJustification(savedJustification)

            if (savedJustification.observationsBudget) {
              methods.setValue('observationsBudget', savedJustification.observationsBudget)
            }
            if (savedJustification.observationsS1) {
              methods.setValue('observationsWeek', savedJustification.observationsS1)
            }

            // Sobrescreve com o snapshot de valores da justificativa salva (momento em que foi criada)
            if (savedJustification.previewAmountS1 != null) {
              methods.setValue('previewWeek', savedJustification.previewAmountS1.toFixed(2))
            }
            if (savedJustification.previewAmount != null) {
              methods.setValue('actualPreviewWeek', savedJustification.previewAmount.toFixed(2))
            }
            if (savedJustification.deviationPreviewAmountS1 != null) {
              methods.setValue('deviationVsPreviewWeek', savedJustification.deviationPreviewAmountS1.toFixed(2))
            }
          }
        } catch {
          // Mantém os valores padrão do formulário em caso de erro
        }
      }

      // Libera a busca dos tipos de desvio mesmo sem justificativa salva (usa o valor do grid)
      setIsJustificationResolved(true)
    }

    initializeForm()
  }, [data, open, methods, branchCode, economicGroupCode, accountId, referenceDate])

  // Aplica o mapeamento deviationTypeId → code de cada campo quando sua lista estiver carregada
  useEffect(() => {
    if (!justification || deviationTypesBudget.length === 0) return

    const budgetCode = deviationTypesBudget.find((t) => t.id === justification.deviationTypeBudgetId)?.code
    if (budgetCode) methods.setValue('deviationTypeBudget', budgetCode)
  }, [justification, deviationTypesBudget, methods])

  useEffect(() => {
    if (!justification || deviationTypesS1.length === 0) return

    const weekCode = deviationTypesS1.find((t) => t.id === justification.deviationTypeS1Id)?.code
    if (weekCode) methods.setValue('deviationTypeWeek', weekCode)
  }, [justification, deviationTypesS1, methods])

  useEffect(() => {
    if (!open) {
      methods.reset(initialFormValues)
      setJustification(null)
      setIsJustificationResolved(false)
      setJustificationId(null)
      setDeviationTypesBudget([])
      setDeviationTypesS1([])
    }
  }, [open, methods, setDeviationTypesBudget, setDeviationTypesS1])

  return (
    <>
      <BaseModal2
        open={open}
        title="Justificar Desvios"
        headerIcon={<NotePencilIcon />}
        onClose={() => setOpenCancelModal(true)}
        width={isMobile ? 'calc(100% - 20px)' : 870}
        heightContainer="auto"
        maxHeightContainer={isMobile ? 'calc(100% - 20px)' : '95%'}
        footer={{
          cancelButtonText: isReadOnly ? 'Voltar' : 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: isReadOnly ? undefined : 'Salvar',
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: isReadOnly ? undefined : () => methods.handleSubmit(onSubmit, onError)(),
          loading: isSaving
        }}
        {...rest}
      >
        <S.Title>Selecione o Tipo de Desvio e as Observações para justificar o(s) desvio(s).</S.Title>

        <FormProvider {...methods}>
          <S.Form>
            <RowBox columns={3}>
              <Input name="economicGroup" label="Grupo Econômico" disabled />
              <Input name="profitCenter" label="Centro de Lucro" disabled />
              <Input name="account" label="Conta" disabled />
              <Input name="budget" label="Budget" maskType="currency" placeholder="R$ 0,00" disabled />
              <Input name="actualPreview" label="Prévia Atual" maskType="currency" placeholder="R$ 0,00" disabled />
              <Input
                name="deviationVsBudget"
                label="Desvio vs Budget"
                maskType="currency"
                placeholder="R$ 0,00"
                disabled
              />
            </RowBox>

            <RowBox>
              <Tooltip
                title={
                  isBudgetDeviationMandatory
                    ? 'Obrigatório informar o Tipo de Desvio quando:\nDesvio vs Budget for superior a R$ 50.000,00 ou inferior a -R$ 50.000,00 e\n% Desvio vs Budget for maior que 5% ou menor que -5%.'
                    : ''
                }
                placement="top"
              >
                <Select
                  name="deviationTypeBudget"
                  label="Tipo de Desvio"
                  options={budgetSelectOptions}
                  required={isBudgetDeviationMandatory}
                  disabled={isReadOnly}
                />
              </Tooltip>
              <Textarea
                name="observationsBudget"
                label="Observações"
                required={isBudgetDeviationMandatory}
                disabled={isReadOnly}
              />
            </RowBox>

            <SeparatorWithLine marginBottom="24px" />

            <RowBox columns={3}>
              <Input name="previewWeek" label="Prévia S-1" maskType="currency" placeholder="R$ 0,00" disabled />
              <Input name="actualPreviewWeek" label="Prévia Atual" maskType="currency" placeholder="R$ 0,00" disabled />
              <Input
                name="deviationVsPreviewWeek"
                label="Desvio vs Prévia S-1"
                maskType="currency"
                placeholder="R$ 0,00"
                disabled
              />
            </RowBox>

            <RowBox>
              <Tooltip
                title={
                  isWeekDeviationMandatory
                    ? 'Obrigatório informar o Tipo de Desvio quando:\nDesvio vs S-1 for superior a R$ 50.000,00 ou inferior a -R$ 50.000,00 e\n% Desvio vs S-1 for maior que 5% ou menor que -5%.'
                    : ''
                }
                placement="top"
              >
                <Select
                  name="deviationTypeWeek"
                  label="Tipo de Desvio"
                  options={weekSelectOptions}
                  required={isWeekDeviationMandatory}
                  disabled={isReadOnly}
                />
              </Tooltip>
              <Textarea
                name="observationsWeek"
                label="Observações"
                required={isWeekDeviationMandatory}
                disabled={isReadOnly}
              />
            </RowBox>
          </S.Form>
        </FormProvider>
      </BaseModal2>

      <ReturnModal
        open={openCancelModal}
        headerIcon={<WarningIcon />}
        headerTitle="Alterações não salvas"
        message="Ao confirmar, os dados inseridos não serão salvos."
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelModal(false)
        }}
      />
    </>
  )
}
